/**
 * Basic Operation Handlers
 * Extracted from projectDataUtils.ts applyOperation
 * Handles create, update, delete, move, reorder, batch operations
 */

import type { ProjectData, ProjectNode, NodeOperation } from '@/shared/types/projectData';
import {
  formatDateToLocalISODate,
  isDateOnlyValue,
  parseDateValuePreservingLocalDay,
} from '@/shared/utils/dates';
import { cloneProjectData } from '@/shared/utils/projectDataUtils';
import { setGanttOrder } from '@/shared/utils/viewOrdering';
import { classifyGanttGroupMoveDescendants } from '@/shared/utils/ganttGroupMovePolicy';

// Import CRUD functions from projectDataUtils
import { createNode, updateNode, deleteNode, deleteExactNodes, moveNode } from '@/shared/utils/projectDataUtils';

function applySiblingOrder(
  cloned: ProjectData,
  parentId: string | null,
  nodeIds: string[]
): void {
  const orderMap = new Map<string, number>();
  nodeIds.forEach((id, idx) => orderMap.set(id, idx));

  const siblings = cloned.nodes.filter(n => (n.parentId || null) === parentId);
  const listed = siblings.filter(n => orderMap.has(n.id));
  const unlisted = siblings.filter(n => !orderMap.has(n.id));

  listed.sort((a, b) => (orderMap.get(a.id)! - orderMap.get(b.id)!));
  const newOrder = [...listed, ...unlisted];
  newOrder.forEach((n, i) => {
    n.order = i;
    setGanttOrder(n, i);
  });
}

function recomputeRootNodeIds(cloned: ProjectData): void {
  cloned.rootNodeIds = cloned.nodes
    .filter(node => !node.parentId)
    .sort((a, b) => {
      const leftOrder = typeof a.order === 'number' ? a.order : 0;
      const rightOrder = typeof b.order === 'number' ? b.order : 0;
      return leftOrder - rightOrder;
    })
    .map(node => node.id);
}

function shiftStoredDateValueByDays(dateValue: string, daysDelta: number): string {
  const shiftedDate = parseDateValuePreservingLocalDay(dateValue);
  shiftedDate.setDate(shiftedDate.getDate() + daysDelta);
  return isDateOnlyValue(dateValue) ? formatDateToLocalISODate(shiftedDate) : shiftedDate.toISOString();
}

export function handleCreate(data: ProjectData, op: NodeOperation): ProjectData {
  // Copy before stripping parentId so undo/history snapshots are not mutated in place.
  // Prefer the operation-level parentId when the backend sends both, but keep nested
  // parentId as the restore fallback when the operation omitted it.
  const nodeData: Partial<ProjectNode> = { ...(op.data || {}) };
  if (op.nodeId) {
    nodeData.id = op.nodeId;
  }
  const parentId = op.parentId !== undefined ? op.parentId : nodeData.parentId;
  delete nodeData.parentId;
  return createNode(data, nodeData, parentId, op.targetIndex);
}

export function handleUpdate(data: ProjectData, op: NodeOperation): ProjectData {
  return op.nodeId ? updateNode(data, op.nodeId, op.data || {}) : data;
}

export function handleDeleteTree(data: ProjectData, op: NodeOperation): ProjectData {
  const record = op as NodeOperation & {
    deletedNodeIds?: string[];
    updatedNodes?: Array<{ nodeId: string; node: Partial<ProjectNode> }>;
  };
  const removed = record.deletedNodeIds?.length
    ? deleteExactNodes(data, new Set(record.deletedNodeIds))
    : op.nodeId ? deleteNode(data, op.nodeId) : data;
  return record.updatedNodes?.length
    ? handleBatchUpdate(removed, { ...op, type: 'batch-update' })
    : removed;
}

export function handleDelete(data: ProjectData, op: NodeOperation): ProjectData {
  const opRecord = op as NodeOperation & Record<string, unknown>;
  const deletedNodeId =
    op.nodeId ||
    (opRecord.deletedNodeId as string | undefined) ||
    (opRecord.deleted_node_id as string | undefined);

  return deletedNodeId ? deleteExactNodes(data, new Set([deletedNodeId])) : data;
}

export function handleMove(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.nodeId) return data;

  // CRITICAL FIX: If backend provides authoritative sibling order (nodeIds array),
  // use reorder logic instead of move to ensure consistent ordering across all clients
  const hasAuthoritativeTargetOrder = Array.isArray(op.nodeIds) && op.nodeIds.length > 0;
  const hasAuthoritativeSourceOrder = op.sourceParentId !== undefined && Array.isArray(op.sourceNodeIds);

  if (hasAuthoritativeTargetOrder || hasAuthoritativeSourceOrder) {
    const cloned = cloneProjectData(data);
    const parentId = op.parentId ?? op.targetParentId ?? null;

    // Update moved node's parent if changed
    const node = cloned.nodes.find(n => n.id === op.nodeId);
    if (node && (node.parentId || null) !== parentId) {
      node.parentId = parentId || undefined;
    }

    if (hasAuthoritativeTargetOrder) {
      applySiblingOrder(cloned, parentId, op.nodeIds ?? []);
    }

    const shouldApplySourceOrder =
      hasAuthoritativeSourceOrder &&
      (op.sourceParentId ?? null) !== parentId;

    if (shouldApplySourceOrder) {
      applySiblingOrder(cloned, op.sourceParentId ?? null, op.sourceNodeIds ?? []);
    }

    recomputeRootNodeIds(cloned);

    return cloned;
  }

  // Fallback to traditional move logic if no authoritative order provided
  return moveNode(data, op.nodeId, op.targetParentId || null, op.targetIndex || 0);
}

export function handleReorder(data: ProjectData, op: NodeOperation): ProjectData {
  // Reorder siblings under a parent using provided nodeIds order
  if (!op.nodeIds || !Array.isArray(op.nodeIds)) return data;
  const cloned = cloneProjectData(data);
  const parentId = op.parentId || null;
  applySiblingOrder(cloned, parentId, op.nodeIds);
  recomputeRootNodeIds(cloned);

  return cloned;
}

export function handleBatchDelete(data: ProjectData, op: NodeOperation): ProjectData {
  const opRecord = op as NodeOperation & Record<string, unknown>;
  const deletedNodeIds =
    op.nodeIds ||
    (opRecord.deletedNodeIds as string[] | undefined) ||
    (opRecord.deleted_node_ids as string[] | undefined) ||
    [];

  return deleteExactNodes(data, new Set(deletedNodeIds));
}

export function handleBatchUpdate(data: ProjectData, op: NodeOperation): ProjectData {
  const upsertAuthoritativeNode = (currentData: ProjectData, nodeId: string, node: Partial<ProjectNode>): ProjectData => {
    const exists = currentData.nodes.some(existingNode => existingNode.id === nodeId);
    if (exists) {
      return updateNode(currentData, nodeId, node);
    }

    const nodeData: Partial<ProjectNode> & { id: string } = { ...node, id: nodeId };
    delete (nodeData as Partial<ProjectNode> & { parentId?: string }).parentId;

    const targetIndex =
      typeof node?.order === 'number' && Number.isFinite(node.order)
        ? node.order
        : undefined;
    return createNode(currentData, nodeData, node?.parentId || undefined, targetIndex);
  };

  // CRITICAL FIX: Prefer fresh node data from backend if available (updatedNodes)
  // This ensures UI updates with the actual stored values from MongoDB
  const opRecord = op as NodeOperation & Record<string, unknown>;
  const updatedNodes = opRecord.updatedNodes as Array<{ nodeId?: string; node?: Partial<ProjectNode> }> | undefined;
  if (updatedNodes && Array.isArray(updatedNodes)) {
    // Backend sent fresh data from MongoDB - use it directly
    const updatedData = updatedNodes.reduce((d: ProjectData, item: { nodeId?: string; node?: Partial<ProjectNode> }) => {
      if (item.node && item.nodeId) {
        return upsertAuthoritativeNode(d, item.nodeId, item.node);
      }
      return d;
    }, data);

    const hasAuthoritativeTargetOrder = Array.isArray(op.nodeIds) && op.nodeIds.length > 0;
    const hasAuthoritativeSourceOrder =
      op.sourceParentId !== undefined && Array.isArray(op.sourceNodeIds);

    if (!hasAuthoritativeTargetOrder && !hasAuthoritativeSourceOrder) {
      return updatedData;
    }

    const cloned = cloneProjectData(updatedData);
    const parentId = op.parentId ?? op.targetParentId ?? null;

    if (hasAuthoritativeTargetOrder) {
      applySiblingOrder(cloned, parentId, op.nodeIds ?? []);
    }

    const shouldApplySourceOrder =
      hasAuthoritativeSourceOrder &&
      (op.sourceParentId ?? null) !== parentId;

    if (shouldApplySourceOrder) {
      applySiblingOrder(cloned, op.sourceParentId ?? null, op.sourceNodeIds ?? []);
    }

    recomputeRootNodeIds(cloned);
    return cloned;
  }
  // Process batch updates with data field (ONE WAY)
  return (op.updates || []).reduce((d, u) => updateNode(d, u.nodeId, u.data), data);
}

export function handleProgressUpdate(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.nodeId || op.progress === undefined) return data;
  return updateNode(data, op.nodeId, { progress: op.progress });
}

export function handleGroupMove(data: ProjectData, op: NodeOperation): ProjectData {
  const opRecord = op as NodeOperation & Record<string, unknown>;
  const updatedNodes = opRecord.updatedNodes;
  if (Array.isArray(updatedNodes) && updatedNodes.length > 0) {
    return applyGroupMoveUpdatedNodes(data, updatedNodes);
  }

  if (!op.nodeId || op.daysDelta === undefined) return data;
  return moveGroupByDays(data, op.nodeId, op.daysDelta);
}

function moveGroupByDays(data: ProjectData, groupId: string, daysDelta: number): ProjectData {
  let result = data;

  const { movableIds } = classifyGanttGroupMoveDescendants(data.nodes, groupId);

  // Update only executable descendants. Notes/documents are immutable anchors,
  // and groups are derived projection rows whose ranges recalculate from children.
  movableIds.forEach(nodeId => {
    const node = result.nodes.find(n => n.id === nodeId);
    if (node) {
      const updates: Partial<ProjectNode> = {};

      if (node.startDate) {
        updates.startDate = shiftStoredDateValueByDays(node.startDate, daysDelta);
      }

      if (node.endDate) {
        updates.endDate = shiftStoredDateValueByDays(node.endDate, daysDelta);
      }

      if (node.date) {
        updates.date = shiftStoredDateValueByDays(node.date, daysDelta);
      }
      if (node.dueDate) {
        updates.dueDate = shiftStoredDateValueByDays(node.dueDate, daysDelta);
      }

      if (Object.keys(updates).length > 0) {
        result = updateNode(result, nodeId, updates);
      }
    }
  });

  return result;
}

type GroupMoveNodePatch = { nodeId: string; node: Partial<ProjectNode> };

function isGroupMoveNodePatch(item: unknown): item is GroupMoveNodePatch {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const record = item as Record<string, unknown>;
  return typeof record.nodeId === 'string'
    && record.node != null
    && typeof record.node === 'object'
    && !Array.isArray(record.node);
}

function isGroupMoveFullNode(item: unknown): item is ProjectNode {
  if (!item || typeof item !== 'object') {
    return false;
  }

  const record = item as Record<string, unknown>;
  return typeof record.id === 'string' && !isGroupMoveNodePatch(item);
}

function applyGroupMoveUpdatedNodes(data: ProjectData, updatedNodes: unknown[]): ProjectData {
  const firstItem = updatedNodes[0];
  if (isGroupMoveNodePatch(firstItem)) {
    return updatedNodes.reduce((current: ProjectData, item) => {
      if (!isGroupMoveNodePatch(item)) {
        return current;
      }
      return updateNode(current, item.nodeId, item.node);
    }, data);
  }

  return updatedNodes.reduce((current: ProjectData, item) => {
    if (!isGroupMoveFullNode(item)) {
      return current;
    }

    const exists = current.nodes.some((existingNode) => existingNode.id === item.id);
    if (exists) {
      return updateNode(current, item.id, item);
    }

    const nodeData: Partial<ProjectNode> & { id: string } = { ...item, id: item.id };
    delete nodeData.parentId;
    return createNode(
      current,
      nodeData,
      item.parentId,
      typeof item.order === 'number' ? item.order : undefined
    );
  }, data);
}
